/**
 * Preference access that cannot throw, backed by the bonded storage provider.
 *
 * Storage can be unavailable (Safari private mode, blocked site data, an
 * embedded context) and a stored value can be corrupt, while several callers run
 * during render or inside a promise chain whose `catch` would read the failure
 * as something else (a failed project create). A failure therefore reads as
 * "nothing stored" and a write is dropped.
 *
 * Values go through `getPreferenceStore()` — the synchronous snapshot over the
 * async `@molecule/app-storage` provider — not `localStorage`: swapping the
 * bonded provider (memory, native, encrypted, a test host) then redirects this
 * state too, and a value written a moment ago is readable synchronously.
 *
 * @module
 */

import { getPreferenceStore } from '@molecule/app-storage'

/**
 * Reads a stored value.
 * @param key - The storage key.
 * @returns The value, or null when it is absent, not a string, or storage is
 *   unavailable.
 */
export function safeStorageGet(key: string): string | null {
  const value = getPreferenceStore().get<unknown>(key)
  return typeof value === 'string' ? value : null
}

/**
 * Stores a value; a storage failure is ignored.
 * @param key - The storage key.
 * @param value - The value to store.
 */
export function safeStorageSet(key: string, value: string): void {
  getPreferenceStore().set(key, value)
}

/**
 * Removes a stored value; a storage failure is ignored.
 * @param key - The storage key.
 */
export function safeStorageRemove(key: string): void {
  getPreferenceStore().remove(key)
}
